import { useEffect, useMemo, useState } from 'react';
import { CarePlanCycleType } from 'shared/types/graphql';
import { useManageMembershipContext } from '../components/ManageMembership/context/ManageMembershipContext';
import { PaymentsMade } from '../components/ManageMembership/types';
import {
  calcAccountCredit,
  calcDiscount,
  calcExams,
  calcFees,
  calcPaymentsMade,
  calcRemainingMembershipBalance,
  calcSubtotal,
  calcTeletriage,
  calcTotalSavings,
  ItemInfo,
  calcAndSetBalanceDue,
  calcTotalBenefitUsage,
} from '../utils/membershipCalculator';
import { useCareEnrollmentInfo } from './useCareEnrollmentInfo';
import { UsePetBenefitCareBenefit } from './usePetBenefits';

interface UseCancellationCalculatorInput {
  enrollmentId: string;
  benefitUsages: UsePetBenefitCareBenefit[];
}

interface UseCancellationCalculatorOutput {
  hasPlanDiscount: boolean;
  accountCredit: ItemInfo;
  exams: ItemInfo;
  discount: ItemInfo;
  teletriage: ItemInfo;
  fees: ItemInfo;
  subtotal: ItemInfo;
  totalBenefitUsage: number;
  paymentsMade: PaymentsMade;
  balanceDue: number;
  balanceDueWithoutManualAdjustment: number;
  totalSavings: number;
  remainingMembershipBalance: PaymentsMade;
  isLoading: boolean;
  billingCycle?: CarePlanCycleType;
  disputedPaymentCount: number;
  refundedPaymentCount: number;
}

export const useCancellationCalculator = ({
  enrollmentId,
  benefitUsages,
}: UseCancellationCalculatorInput): UseCancellationCalculatorOutput => {
  const { setBalanceDue, applyDiscounts, includeFees, includeManualAdjustment, manualAdjustment } =
    useManageMembershipContext();
  const [balanceDueWithoutManualAdjustment, setBalanceDueWithoutManualAdjustment] = useState(0);
  const [internalBalanceDue, setInternalBalanceDue] = useState(0);
  const manualAdjustmentValue = useMemo(() => Number(manualAdjustment), [manualAdjustment]);

  const {
    hasPlanDiscount,
    planDiscount,
    plan,
    planPricing,
    validPayments,
    disputedPaymentCount,
    refundedPaymentCount,
    isLoading,
    startDate,
  } = useCareEnrollmentInfo({
    enrollmentId,
  });

  const accountCredit: ItemInfo = useMemo(() => {
    return calcAccountCredit({ benefitUsages, planDiscount });
  }, [benefitUsages, planDiscount]);

  const exams: ItemInfo = useMemo(() => {
    return calcExams({ benefitUsages, planDiscount });
  }, [benefitUsages, planDiscount]);

  const discount: ItemInfo = useMemo(() => {
    return calcDiscount({ benefitUsages });
  }, [benefitUsages]);

  const teletriage: ItemInfo = useMemo(() => {
    return calcTeletriage({
      startDate,
      planBenefits: plan?.planBenefits,
    });
  }, [startDate, plan?.planBenefits]);

  const paymentsMade: PaymentsMade = useMemo(() => {
    return calcPaymentsMade({
      payments: validPayments,
      pricePerRenewal: planPricing?.pricePerRenewal,
      renewalCycle: planPricing?.renewalCycle,
      billingCycle: planPricing?.billingCycle,
    });
  }, [validPayments, planPricing?.pricePerRenewal, planPricing?.renewalCycle, planPricing?.billingCycle]);

  const subtotal: ItemInfo = useMemo(() => {
    return calcSubtotal({ accountCredit, discount, exams, paymentsMade, teletriage });
  }, [accountCredit, discount, exams, paymentsMade, teletriage]);

  const totalBenefitUsage = useMemo(() => {
    return calcTotalBenefitUsage({
      accountCredit,
      exams,
      discount,
      teletriage,
    });
  }, [accountCredit, exams, discount, teletriage]);

  const fees: ItemInfo = useMemo(() => {
    return calcFees({
      includeFees,
      paymentsMadeTotal: paymentsMade.total,
    });
  }, [includeFees, paymentsMade.total]);

  const totalSavings: number = useMemo(() => {
    return calcTotalSavings({
      accountCreditSavings: accountCredit.savings,
      discountUsedValue: discount.usedValue,
      examsSavings: exams.savings,
    });
  }, [accountCredit.savings, discount.usedValue, exams.savings]);

  useEffect(() => {
    const { amountToUse, amountToUseWithoutManualAdjustment } = calcAndSetBalanceDue({
      subtotal,
      discount,
      totalSavings,
      planDiscount: hasPlanDiscount,
      includeFees,
      fees,
      includeManualAdjustment,
      manualAdjustmentValue,
      applyDiscounts,
    });
    setBalanceDue(amountToUse);
    setInternalBalanceDue(amountToUse);
    setBalanceDueWithoutManualAdjustment(amountToUseWithoutManualAdjustment);
  }, [
    applyDiscounts,
    discount,
    fees,
    hasPlanDiscount,
    includeFees,
    includeManualAdjustment,
    manualAdjustment,
    manualAdjustmentValue,
    planDiscount,
    setBalanceDue,
    subtotal,
    totalSavings,
  ]);

  const remainingMembershipBalance: PaymentsMade = useMemo(() => {
    return calcRemainingMembershipBalance({
      paymentsMadeTotal: paymentsMade.total,
      pricePerRenewal: planPricing?.pricePerRenewal,
      renewalCycle: planPricing?.renewalCycle,
    });
  }, [paymentsMade.total, planPricing?.pricePerRenewal, planPricing?.renewalCycle]);

  return {
    hasPlanDiscount,
    accountCredit,
    exams,
    discount,
    teletriage,
    fees,
    subtotal,
    paymentsMade,
    balanceDue: internalBalanceDue,
    totalBenefitUsage,
    balanceDueWithoutManualAdjustment,
    totalSavings,
    remainingMembershipBalance,
    isLoading,
    billingCycle: planPricing?.billingCycle,
    disputedPaymentCount,
    refundedPaymentCount,
  };
};
